import { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from './api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('emr_token');
    const savedUser = localStorage.getItem('emr_user');
    
    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
        // Verify token is still valid
        authAPI.getMe()
          .then(res => setUser(res.data))
          .catch(() => {
            localStorage.removeItem('emr_token');
            localStorage.removeItem('emr_user');
            setUser(null);
          })
          .finally(() => setLoading(false));
      } catch {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await authAPI.login(email, password);
      const { token, user: userData } = response.data;
      
      localStorage.setItem('emr_token', token);
      localStorage.setItem('emr_user', JSON.stringify(userData));
      setUser(userData);
      
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const register = async (data) => {
    try {
      setError(null);
      await authAPI.register(data);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Registration failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('emr_token');
    localStorage.removeItem('emr_user');
    setUser(null);
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    
    const rolePermissions = {
      admin: ['*'],
      physician: ['patients:*', 'forms:*', 'submissions:*', 'reports:read', 'users:read'],
      nurse: ['patients:read', 'patients:update', 'forms:read', 'submissions:create', 'submissions:read'],
      clerk: ['patients:read', 'patients:create', 'forms:read', 'submissions:create', 'submissions:read'],
      readonly: ['patients:read', 'forms:read', 'submissions:read'],
    };

    const perms = rolePermissions[user.role] || [];
    
    if (perms.includes('*')) return true;
    if (perms.includes(permission)) return true;
    
    // Check wildcard
    const [resource] = permission.split(':');
    if (perms.includes(`${resource}:*`)) return true;
    
    return false;
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    hasPermission,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
